/**
 * THE gag owner-picker rule — which people an "Assigned to" / Owner select
 * offers, and what picking one does to a BLANK sub-department.
 *
 * Extracted 2026-10-18 out of `OwnerSubDeptFields.js` (body moved, not
 * rewritten) so the Breakdown row's Owner cell (`GagRowEditCell.js`) asks the
 * SAME question the panel, the create modal and `teams/TaskEditor.js` ask.
 * Owner: the row and the panel must not offer different lists for one field.
 * The rules (numbered as in `OwnerSubDeptFields`' docblock, which stays the
 * place they are explained):
 *  1. No sub-department chosen → ALL department users are offered.
 *  2. Sub-department chosen → only users in THAT sub-department.
 *  3. STRICT: users with no sub-department drop out while a filter is active,
 *     except the keep-listed entries.
 *  4. The gag's persisted owner and the current selection are ALWAYS listed,
 *     suffixed "(other sub-dept)" on a mismatch — never silently cleared.
 *  5. Picking an owner fills a BLANK sub-department from the owner's own.
 * Permission modes: `canManage` → the full list under 1–4; a non-manager →
 * Unassigned + "Me" (only when `selfClaimAllowed`) + the keep-listed owner; a
 * manager missing from the list (a site admin) also gets "Me".
 *
 * 🔑 It decides NO permission. Whether an owner control renders at all is the
 * caller's (`canAssignOwner` / `can_manage`, matching the server's PUT ladder).
 * All values are STRINGS ('' = none), the callers' all-strings invariant.
 */
export const MISMATCH_SUFFIX = ' (other sub-dept)';

const subDeptOf = (u) => (u && u.sub_department_id != null ? String(u.sub_department_id) : '');

/**
 * The owner `<option>`s AFTER the "Unassigned" entry: `[{ value, label }]`.
 * - users: assignable users `[{ id, display_name, sub_department_id }]`
 * - currentOwner: `{ id, name } | null` — the gag's PERSISTED owner
 */
export function buildGagOwnerOptions({
  users,
  canManage,
  userId,
  subDeptValue,
  ownerValue,
  currentOwner = null,
  selfClaimAllowed = true,
}) {
  const list = Array.isArray(users) ? users : [];
  const byId = new Map(list.map((u) => [u.id, u]));
  const filterActive = subDeptValue !== '';
  const decorate = (id, name) => (id === userId ? `${name} (me)` : name);

  const options = [];
  const seen = new Set();
  const push = (id, label, mismatched) => {
    const v = String(id);
    if (seen.has(v)) return;
    seen.add(v);
    options.push({ value: v, label: mismatched ? label + MISMATCH_SUFFIX : label });
  };

  // "Me" first (right after Unassigned) — see the permission modes above.
  if (Number.isFinite(userId) && selfClaimAllowed && (!canManage || !byId.has(userId))) {
    const me = byId.get(userId);
    push(
      userId,
      me ? decorate(userId, me.display_name) : 'Me',
      filterActive && subDeptOf(me) !== subDeptValue
    );
  }

  // Managers: full list under the standard filter rules (1–3).
  if (canManage) {
    for (const u of list) {
      if (!filterActive || subDeptOf(u) === subDeptValue) {
        push(u.id, decorate(u.id, u.display_name), false);
      }
    }
  }

  // Keep-listed entries (rule 4). An owner no longer in the assignable list
  // (e.g. deactivated since assignment) stays visible via currentOwner.name.
  const keep = [];
  if (currentOwner && currentOwner.id != null) keep.push(currentOwner);
  if (ownerValue !== '') {
    const oid = parseInt(ownerValue, 10);
    keep.push({
      id: oid,
      name:
        byId.get(oid)?.display_name ||
        (currentOwner && currentOwner.id === oid ? currentOwner.name : `User ${ownerValue}`),
    });
  }
  for (const k of keep) {
    const u = byId.get(k.id);
    push(
      k.id,
      decorate(k.id, u?.display_name || k.name),
      filterActive && subDeptOf(u) !== subDeptValue
    );
  }
  return options;
}

/**
 * Rule 5 — the patch for picking owner `ownerValue`: `{ owner_id }`, plus
 * `sub_department_id` from the owner's own when the sub-department is BLANK
 * (fill-when-blank, mirrors the server's autoFillSubDeptFromOwner).
 */
export function gagOwnerPickPatch({ ownerValue, subDeptValue, users }) {
  const patch = { owner_id: ownerValue };
  if (ownerValue && subDeptValue === '') {
    const list = Array.isArray(users) ? users : [];
    const u = list.find((x) => x.id === parseInt(ownerValue, 10));
    if (u && u.sub_department_id != null) {
      patch.sub_department_id = String(u.sub_department_id);
    }
  }
  return patch;
}
